import {
  OrganizationProvider,
  SimDetailsPage,
  SimInventoryApiTypes,
  type SimInventoryCommonTypes,
} from '@hologram-hyper-dashboard/components';
import { BUTTON_CLICK } from 'common-js/analytics/actionTypes';
import sendAnalyticsEvent from 'common-js/analytics/analytics';
import useGetDeviceById from 'common-js/api/devices/useGetDeviceById';
import { getUserContextDataMemoized } from 'common-js/api/util';
import { FullHeightCalculatorHOC } from 'common-js/components';
import {
  BILLING_DATA_PLAN,
  SIM_COVERAGE,
  SIM_DETAILS,
  SIM_INVENTORY,
  SIM_LOCATION_HISTORY,
  SIM_PRICING,
  SIM_PROFILES,
  SIM_RECENT_SESSIONS,
  SIM_ROUTES,
  SIM_SIMS_AND_DEVICES,
  SIM_USAGE,
  SIM_WEBHOOKS,
  USAGE_BY_SESSION,
  pathMatchesWithIdsReplaced,
} from 'common-js/constants/paths';
import { useAppDispatch, useAppSelector } from 'common-js/hooks';
import useOnAllTasksSuccessful from 'common-js/hooks/useOnAllTasksSuccessful';
import { setCurrentDevice } from 'common-js/reducers/currentDevice/actions';
import { setDeviceFilter } from 'common-js/reducers/logFilter/actions';
import { openModal } from 'common-js/reducers/modal/actions';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { browserHistory } from 'react-router';
import Routes from 'src/js/device/Routes/Routes';
import { OpenModalCallback } from 'types/hyper-dashboard';
import SubMenu from '../components/SubMenu';
import SimToLegacyAdapter from './SimToLegacyAdapter';

const RoutesWrapper: FC = () => (
  <div className="legacyRoutesWrapper">
    <Routes />
  </div>
);

interface SimDetailsProps {
  params: { simId: string };
  location: { state: {}; pathname: string };
}

const SimDetails: FC<SimDetailsProps> = ({ params, location }) => {
  const dispatch = useAppDispatch();
  const userContext = useAppSelector(getUserContextDataMemoized);
  const orgId = userContext.isInOrgContext ? userContext.orgId : userContext.userOrgId;
  const { simId } = params;

  const [deviceInfo, setDeviceInfo] = useState<
    | (SimInventoryApiTypes.SimsDaoDeviceOverview & SimInventoryApiTypes.SimsDaoDeviceDetails)
    | undefined
  >();

  const path = location.pathname;

  const getViewFromUrl = useCallback((url) => {
    if (pathMatchesWithIdsReplaced(SIM_SIMS_AND_DEVICES, url)) {
      return 'simanddevices';
    }
    if (pathMatchesWithIdsReplaced(SIM_PROFILES, url)) {
      return 'profiles';
    }
    if (pathMatchesWithIdsReplaced(SIM_RECENT_SESSIONS, url)) {
      return 'recentsessions';
    }
    if (pathMatchesWithIdsReplaced(SIM_USAGE, url)) {
      return 'usage';
    }
    if (pathMatchesWithIdsReplaced(SIM_LOCATION_HISTORY, url)) {
      return 'locationhistory';
    }
    if (pathMatchesWithIdsReplaced(SIM_COVERAGE, url)) {
      return 'coverage';
    }
    if (pathMatchesWithIdsReplaced(SIM_PRICING, url)) {
      return 'pricing';
    }
    if (pathMatchesWithIdsReplaced(SIM_WEBHOOKS, url)) {
      return 'webhooks';
    }
    if (pathMatchesWithIdsReplaced(SIM_ROUTES, url)) {
      return 'routes';
    }
    if (pathMatchesWithIdsReplaced(SIM_DETAILS, url)) {
      return 'details';
    }

    return 'details';
  }, []);

  const view: SimInventoryCommonTypes.SimDetailsView = getViewFromUrl(path);

  // TODO: Refactor these to `to` links in Hyper Dashboard
  const onNavigate = useCallback(
    (url: string) => {
      // TODO: Hacky, fix with proper routing when we redo nav
      const urlSplit = url.split('/');
      const orgIdx = urlSplit.indexOf('org');
      urlSplit[orgIdx + 1] = orgId;

      const urlToUse = urlSplit.join('/');

      sendAnalyticsEvent({
        type: BUTTON_CLICK,
        data: {
          name: `SIM Details - ${getViewFromUrl(urlToUse)}`,
        },
      });

      browserHistory.push({ pathname: urlToUse });
    },
    [getViewFromUrl, orgId]
  );

  const handleModalOpen = useCallback<OpenModalCallback>(
    ({ modalName, modalProps }) => {
      dispatch(openModal(modalName, modalProps, 'narrow no-style'));
    },
    [dispatch]
  );

  const refreshRef = useRef<SimInventoryCommonTypes.OnSimsModified>(null);

  const onSimsModified = useCallback<SimInventoryCommonTypes.OnSimsModified>(
    (simsModified?: Array<HologramSimId>) => {
      if (refreshRef.current) {
        refreshRef.current(simsModified);
      }
    },
    []
  );

  useOnAllTasksSuccessful(onSimsModified);

  const onDeviceChange = useCallback(
    (
      newDeviceInfo?: SimInventoryApiTypes.SimsDaoDeviceOverview &
        SimInventoryApiTypes.SimsDaoDeviceDetails
    ) => {
      setDeviceInfo(newDeviceInfo);
    },
    []
  );

  const getDevice = useGetDeviceById();

  useEffect(() => {
    const setSelectedDevice = async (deviceId: DeviceId) => {
      const device = await getDevice(deviceId);
      dispatch(setCurrentDevice(device));
    };

    if (deviceInfo?.claimedDeviceId) {
      dispatch(setDeviceFilter(deviceInfo.claimedDeviceId));
      setSelectedDevice(deviceInfo.claimedDeviceId);
    }

    return () => {
      // Devices doesn't clear the selected device, so neither will we
      dispatch(setDeviceFilter(null));
    };
  }, [deviceInfo?.claimedDeviceId, dispatch, getDevice]);

  return (
    <OrganizationProvider organizationId={orgId}>
      <SubMenu simId={simId} deviceInfo={deviceInfo} />
      <FullHeightCalculatorHOC
        accountForDrawer
        relative
        classNames="full-height-container--alt"
        alwaysScrollBar
      >
        <div className="container">
          <SimToLegacyAdapter>
            <SimDetailsPage
              hologramSimId={parseInt(simId, 10)}
              viewKey={view}
              onNavigate={onNavigate}
              simDetailBaseUrl={SIM_DETAILS}
              simInventoryBaseUrl={SIM_INVENTORY}
              dataPlanUrl={BILLING_DATA_PLAN}
              handleModalOpen={handleModalOpen}
              ref={refreshRef}
              onDeviceChange={onDeviceChange}
              RoutesComponent={RoutesWrapper}
              usageByRecentSessionUrl={USAGE_BY_SESSION}
            />
          </SimToLegacyAdapter>
        </div>
      </FullHeightCalculatorHOC>
    </OrganizationProvider>
  );
};

export default SimDetails;
